/**
 * Shared MapLibre basemap constants — the leaf module both the raster
 * style (:file:`style.ts`) and the vector style (:file:`style-vector.ts`)
 * import from, so neither has to import the other (keeping the map
 * module's dependency graph **acyclic** — the cold-start discipline).
 *
 * These are the source / layer ids and the glyphs-URL primitive that are
 * genuinely *shared* between the two basemap kinds. :file:`style.ts`
 * re-exports them all so existing callers that import from
 * ``@/map/style`` are unaffected (back-compat); new vector code imports
 * straight from here.
 *
 * Cross-references:
 *   - REQ-UI-002 (MapLibre GL JS); REQ-UI-035 (BFF-only glyphs).
 *   - Issue #205 (BFF glyphs endpoint).
 *   - ADR-016 §4 + 2026-06-10 amendment.
 */

/**
 * MapLibre source ID for the **raster** basemap source. Exported so
 * :func:`applyMapTheme` and the layer-preservation regression test can
 * probe it via ``map.getSource(...)``.
 */
export const MAP_BASE_SOURCE_ID = "meridian-base";

/**
 * MapLibre layer ID for the solid background (paints while tiles load
 * and past tile coverage). Shared by both basemap kinds —
 * :func:`applyMapTheme` flips its ``background-color`` between the
 * light/dark map-background tokens regardless of raster vs. vector.
 */
export const MAP_BACKGROUND_LAYER_ID = "background";

/**
 * MapLibre layer ID for the **raster** basemap layer.
 * :func:`applyMapTheme` applies the dev darkening filter to this layer
 * when no production dark tile layer is configured (raster path only).
 */
export const MAP_BASE_RASTER_LAYER_ID = "meridian-base-raster";

/**
 * Default base path for the BFF glyphs endpoint (issue #205).
 *
 * Same-origin per REQ-UI-035 — the BFF serves the pre-generated SDF font
 * atlas at ``/api/glyphs/<fontstack>/<range>.pbf``. The raster path's
 * cluster-count symbol layer and the vector path's label layers both
 * request the committed ``DM Sans Medium`` fontstack from here.
 *
 * Cross-references:
 *   - Issue #205 (BFF glyphs endpoint).
 *   - REQ-UI-010 (cluster count indicator) / REQ-UI-035 (BFF-only).
 */
export const DEFAULT_GLYPHS_BASE_URL = "/api/glyphs";

/**
 * Compose the MapLibre ``glyphs`` URL template from a base path —
 * normalising a trailing slash and appending the
 * ``{fontstack}/{range}.pbf`` placeholders MapLibre substitutes per
 * symbol layer.
 *
 * Shared by the raster style (:func:`buildMapStyle`) and the vector
 * style (:func:`buildVectorBasemap`) so both request the BFF-served SDF
 * atlas identically — same-origin per REQ-UI-035, no web-font CDN
 * (air-gap posture, CLAUDE.md §8.2).
 *
 * @param glyphsBaseUrl - The glyphs endpoint base (e.g. ``/api/glyphs``).
 * @returns The ``glyphs`` URL template (``…/{fontstack}/{range}.pbf``).
 *
 * Cross-references:
 *   - Issue #205 (BFF glyphs endpoint); REQ-UI-035 (BFF-only).
 */
export function glyphsUrlFromBase(glyphsBaseUrl: string): string {
  const normalized = glyphsBaseUrl.endsWith("/") ? glyphsBaseUrl.slice(0, -1) : glyphsBaseUrl;
  return `${normalized}/{fontstack}/{range}.pbf`;
}
